import React, { useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { BigButtonCard } from '@winnin/winnin_ui.components.big-button-card'
import { Button } from '@winnin/winnin_ui.components.button'
import { Chip } from '@winnin/winnin_ui.components.chip'
import { Modal } from '@winnin/winnin_ui.components.modal'
import { Title } from '@winnin/winnin_ui.components.title'

import { SMALL_MODAL } from 'consts/modalStyles'
import { useAuth } from 'contexts/AuthContext'
import { useWinninAIReport } from 'contexts/WinninAIReportContext'
import { T, useTranslate } from 'lib/tolgee'
import { FeatureFlagsStore } from 'stores'
import { TOLGEE_GLOBALS } from 'utils'

import AvailableReports from './components/AvailableReports'
import GenerateReportButton from './components/GenerateReportButton'
import NextButtonReport from './components/NextButtonReport'
import {
  GET_GENERATED_REPORTS_COUNT,
  GET_CREATORS_SEARCH_ELIGIBILITY,
  GET_TERRITORY_ANALYSIS_ELIGIBILITY,
  GET_TREND_ANALYSIS_ELIGIBILITY,
  GET_WEEKLY_AGENDA_ELIGIBILITY,
  WINNIN_AI_REPORT_WALLET_QUERY,
} from './query'
import './index.scss'

const TOLGEE_PREFIX = 'GenerateWinninAIReportModal'

const LANGUAGES = [
  {
    label: TOLGEE_GLOBALS.por,
    value: 'por',
  },
  {
    label: TOLGEE_GLOBALS.eng,
    value: 'eng',
  },
  {
    label: TOLGEE_GLOBALS.spa,
    value: 'spa',
  },
]

const NEXT_BUTTON_ALLOWED_TYPES = {
  territory_analysis: 'territory_analysis',
  search_creators: 'search_creators',
}

export default function GenerateWinninAIReportModal() {
  const { winninAIReport, resetWinninAIReport } = useWinninAIReport()
  const { plan } = useAuth()
  const t = useTranslate()

  const [reportType, setReportType] = useState('territory_analysis')
  const [reportLanguage, setReportLanguage] = useState('por')

  const isNewCreditSystemEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isNewCreditSystemEnabled')

  const {
    data: countReportsData,
    error: countErrorReports,
    loading: countLoadingReports,
    refetch: countRefetchReports,
  } = useQuery(GET_GENERATED_REPORTS_COUNT, {
    skip: !isNewCreditSystemEnabled,
    fetchPolicy: 'network-only',
  })

  const { data, loading, error, refetch } = useQuery(WINNIN_AI_REPORT_WALLET_QUERY, {
    fetchPolicy: 'network-only',
    skip: !winninAIReport?.id || isNewCreditSystemEnabled,
  })

  const [loadTerritoryAnalysisEligibility, { loading: territoryAnalysisLoading, data: territoryAnalysisData }] =
    useLazyQuery(GET_TERRITORY_ANALYSIS_ELIGIBILITY, {
      skip: !winninAIReport?.id,
    })

  const [loadWeeklyAgendaEligibility, { loading: weeklyAgendaLoading, data: weeklyAgendaData }] = useLazyQuery(
    GET_WEEKLY_AGENDA_ELIGIBILITY,
    {
      skip: !winninAIReport?.id,
    }
  )

  const [loadTrendAnalysisEligibility, { loading: trendAnalysisLoading, data: trendAnalysisData }] = useLazyQuery(
    GET_TREND_ANALYSIS_ELIGIBILITY,
    {
      skip: !winninAIReport?.id,
    }
  )

  const [loadCreatorsSearchEligibility, { loading: creatorsSearchLoading, data: creatorsSearchData }] = useLazyQuery(
    GET_CREATORS_SEARCH_ELIGIBILITY,
    {
      skip: !winninAIReport?.id,
    }
  )

  const eligibilityVariables = useMemo(
    () => ({
      territoryId: `${winninAIReport?.entity}.${winninAIReport?.id}`,
      ...(winninAIReport?.outputProviders ? { outputProviders: winninAIReport?.outputProviders } : {}),
    }),
    [winninAIReport?.entity, winninAIReport?.id, winninAIReport?.outputProviders]
  )

  function handleBigButtonClick(type, getEligibility) {
    setReportType(type)

    getEligibility()
  }

  function handleAddChipLanguageClick(language) {
    setReportLanguage(language)
  }

  function onRequestClose() {
    resetWinninAIReport()
  }

  const useCases = useMemo(
    () => [
      {
        label: TOLGEE_GLOBALS.territoryAnalysis,
        value: 'territory_analysis',
        description: TOLGEE_GLOBALS.territoryAnalysisDescriptionWithDate,
        icon: 'videoOutline',
        disabled: false,
        getEligibility: () =>
          loadTerritoryAnalysisEligibility({
            variables: {
              data: { dateRange: '12', ...eligibilityVariables },
            },
          }),
      },
      {
        label: TOLGEE_GLOBALS.weeklyAgendaNew,
        value: 'weekly_schedule',
        description: TOLGEE_GLOBALS.weeklyAgendaDescriptionWithDate,
        icon: 'agenda',
        getEligibility: () =>
          loadWeeklyAgendaEligibility({
            variables: { data: eligibilityVariables },
          }),
      },
      {
        label: TOLGEE_GLOBALS.trendAnalysis,
        value: 'trend_analysis',
        description: TOLGEE_GLOBALS.trendAnalysisDescriptionWithDate,
        icon: 'trending',
        getEligibility: () =>
          loadTrendAnalysisEligibility({
            variables: { data: eligibilityVariables },
          }),
      },
      {
        label: TOLGEE_GLOBALS.creatorsSearch,
        value: 'search_creators',
        description: TOLGEE_GLOBALS.creatorsSearchDescription,
        icon: 'creator',
        getEligibility: () =>
          loadCreatorsSearchEligibility({
            variables: { data: eligibilityVariables },
          }),
      },
    ],
    [
      eligibilityVariables,
      loadCreatorsSearchEligibility,
      loadTerritoryAnalysisEligibility,
      loadTrendAnalysisEligibility,
      loadWeeklyAgendaEligibility,
    ]
  )

  const shouldRenderNextButton = NEXT_BUTTON_ALLOWED_TYPES[reportType]

  const shouldBlockNextButtonNotEligible = useMemo(() => {
    if (reportType === 'territory_analysis') {
      return !territoryAnalysisData?.getAutomaticJobTerritoryAnalysisEligibility
    }

    if (reportType === 'search_creators') {
      return !creatorsSearchData?.getAutomaticJobSearchCreatorsEligibility
    }

    return false
  }, [creatorsSearchData, reportType, territoryAnalysisData])

  const shouldBlockGenerateReportButtonNotEligible = useMemo(() => {
    if (reportType === 'weekly_schedule') {
      return !weeklyAgendaData?.getAutomaticJobWeeklyScheduleEligibility
    }

    if (reportType === 'trend_analysis') {
      return !trendAnalysisData?.getAutomaticJobTrendAnalysisEligibility
    }

    return false
  }, [reportType, weeklyAgendaData, trendAnalysisData])

  const totalZaiReportsFromPlan = plan?.workspaceZaiReports || 0
  const totalZaiReportsGenerated = countReportsData?.getAutomaticJobCount || 0
  const totalZaiReportsAvailable = totalZaiReportsFromPlan - totalZaiReportsGenerated

  const shouldBlockButton = useMemo(() => {
    if (!isNewCreditSystemEnabled) {
      return data?.myWallet?.amount <= 0
    }
    return totalZaiReportsAvailable <= 0 || !!countErrorReports
  }, [countErrorReports, data?.myWallet?.amount, isNewCreditSystemEnabled, totalZaiReportsAvailable])

  useEffect(() => {
    const { getEligibility } = useCases.find(({ value }) => value === 'territory_analysis')
    handleBigButtonClick('territory_analysis', getEligibility)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal
      isOpen={
        winninAIReport?.id && winninAIReport?.entity && !winninAIReport?.reportType && !winninAIReport?.outputLanguage
      }
      onRequestClose={onRequestClose}
      appElement={document.body}
      style={SMALL_MODAL}
      className="GenerateWinninAIReportModal"
    >
      <div className="GenerateWinninAIReportModal-content">
        <Title tag="h3" weight="bold">
          <T keyName={`${TOLGEE_PREFIX}-modalTitle`}>Winnin Intelligence Report</T>
        </Title>
        <div className="GenerateWinninAIReportModal-FirstModalContent">
          <div>
            <p className="GenerateWinninAIReportModal-FirstModalContent-description">
              {winninAIReport?.name ? (
                <T
                  keyName={`${TOLGEE_PREFIX}-modalDescription-withName`}
                  parameters={{
                    name: winninAIReport?.name,
                    span: <span />,
                  }}
                >
                  {
                    'Encontre oportunidades rapidamente sobre <span>{name}</span> com insights baseados em dados e economize seu tempo fazendo análises!'
                  }
                </T>
              ) : (
                <T keyName={`${TOLGEE_PREFIX}-modalDescription`}>
                  Encontre oportunidades rapidamente com insights baseados em dados e economize seu tempo fazendo
                  análises!
                </T>
              )}
            </p>
          </div>

          {isNewCreditSystemEnabled ? (
            <AvailableReports
              error={countErrorReports}
              loading={countLoadingReports}
              refetch={countRefetchReports}
              totalZaiReportsAvailable={totalZaiReportsAvailable}
            />
          ) : (
            <AvailableReports
              error={error}
              loading={loading}
              refetch={refetch}
              totalZaiReportsAvailable={data?.myWallet?.amount}
            />
          )}

          <div>
            <span className="GenerateWinninAIReportModal-FirstModalContent-text">
              <T keyName={`${TOLGEE_PREFIX}-modalUseCase`}>Caso de uso</T>
            </span>
            <div className="GenerateWinninAIReportModal-FirstModalContent-card-container">
              {useCases.map(({ label, value, description, icon, disabled, getEligibility }) => {
                return (
                  <BigButtonCard
                    key={value}
                    description={t(description)}
                    icon={icon}
                    title={t(label)}
                    isActive={reportType === value}
                    onClick={() => handleBigButtonClick(value, getEligibility)}
                    width={257}
                    isDisabled={disabled}
                    className="GenerateWinninAIReportModal-FirstModalContent-big-button"
                  />
                )
              })}
            </div>
          </div>

          <div>
            <span className="GenerateWinninAIReportModal-FirstModalContent-text">
              <T keyName={`${TOLGEE_PREFIX}-modalLanguage`}>Idioma do report</T>
            </span>
            <div className="GenerateWinninAIReportModal-FirstModalContent-chip-container">
              {LANGUAGES.map(({ label, value }) => (
                <Chip
                  key={value}
                  title={t(label)}
                  isActive={reportLanguage === value}
                  handleClick={() => handleAddChipLanguageClick(value)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="GenerateWinninAIReportModal-actions">
        <Button onClick={resetWinninAIReport} appearance="link" variation="faded">
          <T keyName={TOLGEE_GLOBALS.cancel.key}>{TOLGEE_GLOBALS.cancel.defaultValue}</T>
        </Button>

        {shouldRenderNextButton ? (
          <NextButtonReport
            reportType={reportType}
            reportLanguage={reportLanguage}
            isNotEligible={shouldBlockNextButtonNotEligible}
            loading={territoryAnalysisLoading || creatorsSearchLoading}
            isBlocked={shouldBlockButton}
          />
        ) : (
          <GenerateReportButton
            reportType={reportType}
            reportLanguage={reportLanguage}
            isNotEligible={shouldBlockGenerateReportButtonNotEligible}
            loading={weeklyAgendaLoading || trendAnalysisLoading}
            isBlocked={shouldBlockButton}
          />
        )}
      </div>
    </Modal>
  )
}
